/**
 * TypeAheadLookup — reusable live-search lookup for any backend collection.
 * Props:
 *   value         — current code/id value
 *   displayValue  — human-readable label shown in the field (optional)
 *   onChange      — (code, record) => void
 *   endpoint      — e.g. '/api/data/categories'
 *   searchParam   — query param name for search (default: 'search')
 *   valueField    — field used as the code (default: 'code')
 *   labelField    — field shown as primary label (default: 'description')
 *   secondaryField— smaller secondary text (optional)
 *   placeholder   — input placeholder text
 *   disabled      — make read-only
 *   pageSize      — max results (default: 20)
 *   className     — extra classes on the wrapper
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { Search, X, ChevronDown } from 'lucide-react';

export default function TypeAheadLookup({
  value = '',
  displayValue,
  onChange,
  endpoint,
  searchParam = 'search',
  valueField = 'code',
  labelField = 'description',
  secondaryField,
  placeholder = 'Type to search…',
  disabled = false,
  pageSize = 20,
  className = '',
  excludeValues = [],
  clearOnSelect = false,
  'data-testid': testId,
}) {
  const [inputVal, setInputVal] = useState(value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Show code:label when selected
  const displayLabel = displayValue != null ? displayValue
    : value ? value : '';

  // Sync external value changes
  useEffect(() => {
    setInputVal(displayValue ?? value ?? '');
  }, [value, displayValue]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        // Reset to last confirmed value if user typed without selecting
        setInputVal(displayValue ?? value ?? '');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [value, displayValue]);

  const doSearch = useCallback(async (q) => {
    if (!endpoint) return;
    setLoading(true);
    try {
      const res = await api.get(endpoint, {
        params: { [searchParam]: q || '', page_size: pageSize }
      });
      const list = Array.isArray(res.data) ? res.data : (res.data.data || []);
      const filtered = excludeValues.length > 0
        ? list.filter(item => !excludeValues.includes(item[valueField]))
        : list;
      setResults(filtered);
      setOpen(true);
      setActiveIdx(-1);
    } catch { setResults([]); setOpen(false); }
    setLoading(false);
  }, [endpoint, searchParam, pageSize]);

  const handleInputChange = (e) => {
    const q = e.target.value;
    setInputVal(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      doSearch(q);
    }, 200);
  };

  const handleFocus = () => {
    setInputVal('');
    // Small delay to avoid race with outside-click handler
    setTimeout(() => doSearch(''), 50);
  };

  const handleClick = () => {
    // If dropdown isn't open, trigger a search to show it
    if (!open && !loading) {
      doSearch(inputVal || '');
    }
  };

  const handleSelect = (record) => {
    const code = record[valueField];
    const label = record[labelField];
    if (clearOnSelect) {
      setInputVal('');
      setResults([]);
    } else {
      const display = `${code}${label ? ` — ${label}` : ''}`;
      setInputVal(display);
    }
    setOpen(false);
    onChange?.(code, record);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setInputVal('');
    setResults([]);
    setOpen(false);
    onChange?.('', null);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && activeIdx >= 0) { e.preventDefault(); handleSelect(results[activeIdx]); }
    if (e.key === 'Escape') { setOpen(false); setInputVal(displayLabel); }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" aria-hidden />
        <input
          ref={inputRef}
          type="text"
          value={inputVal}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          placeholder={disabled ? (displayLabel || '—') : placeholder}
          disabled={disabled}
          data-testid={testId}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-haspopup="listbox"
          className={`h-9 w-full rounded-lg border border-input bg-background pl-9 pr-8 text-sm
            focus:outline-none focus:border-ring focus:ring-2 focus:ring-ring/20
            disabled:bg-muted/40 disabled:cursor-not-allowed disabled:text-muted-foreground
            transition-colors`}
        />
        {/* Clear / loading indicator */}
        <div className="absolute right-2.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {loading && (
            <div className="h-3.5 w-3.5 animate-spin rounded-full border border-muted border-t-primary" />
          )}
          {value && !loading && !disabled && (
            <button onClick={handleClear} aria-label="Clear selection"
              className="text-muted-foreground hover:text-foreground transition-colors">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          {!value && !loading && !disabled && (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          )}
        </div>
      </div>

      {/* Dropdown */}
      {open && results.length > 0 && (
        <div
          role="listbox"
          className="absolute z-[200] w-full mt-1 bg-card border border-border rounded-xl shadow-lg overflow-hidden"
          style={{ maxHeight: '220px', overflowY: 'auto' }}
          onMouseDown={e => e.preventDefault()}
        >
          {results.map((record, i) => {
            const code = record[valueField];
            const label = record[labelField];
            const secondary = secondaryField ? record[secondaryField] : null;
            const isActive = i === activeIdx;
            return (
              <div
                key={code || i}
                role="option"
                aria-selected={isActive}
                className={`w-full flex items-start gap-3 px-3 py-2.5 text-left hover:bg-accent transition-colors cursor-pointer ${
                  isActive ? 'bg-accent' : ''
                }`}
                onMouseDown={e => { e.preventDefault(); e.stopPropagation(); handleSelect(record); }}
                onMouseEnter={() => setActiveIdx(i)}
              >
                <span className="font-mono text-xs font-semibold text-primary w-24 flex-shrink-0 pt-0.5 truncate">{code}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{label}</p>
                  {secondary && <p className="text-xs text-muted-foreground truncate">{secondary}</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
